// Create a fresh instance from a layout config (counterpart to migrateDashboardToInstance, which
// imports an existing dashboard). Pure orchestration: the caller injects the create fns, so it's
// unit-testable without React.
import { exportLayoutConfig, type DashboardPanelSlicesState } from "./export-layout-config"
import { serializeWorkspace, type WorkspaceSerializableState } from "./serialize-workspace"
import { createDefaultModules } from "../store/panels-slice"
import { createUuid, type Uuid } from "helpers/types/uuid"
import { warn } from "helpers/dev-logger"
import type { Layout } from "resources/layouts"
import type { DashboardInstance, CreateInstancePayload } from "resources/dashboard-instances"
import type { LayoutConfigV1, WorkspaceStateV1 } from "../contracts/layouts"

// Default name for a brand-new dashboard until the user renames it.
export const NEW_DASHBOARD_NAME = "New dashboard"

// The synthetic dashboard key used only while projecting the default modules into a config — it
// never reaches the store, so any stable string works.
const DEFAULTS_DASHBOARD_KEY = "new"

export interface InstantiateInstanceArgs {
  // Display name for both the layout and the instance.
  name: string
  // The portable layout to persist.
  config: LayoutConfigV1
  // The instance's initial autosave snapshot. Best-effort PUT after create (see below). When
  // omitted, the instance starts with no saved workspace and rebuilds from the layout on open.
  workspace?: WorkspaceStateV1
  createLayout: (name: string, config: LayoutConfigV1) => Promise<Layout>
  createInstance: (payload: CreateInstancePayload) => Promise<DashboardInstance>
  saveWorkspace: (instanceId: string, workspace: WorkspaceStateV1) => Promise<void>
}

/**
 * Create a layout + instance from `config`, returning the new instanceId + server-generated slug.
 *
 * Mirrors migrateDashboardToInstance's error contract: the two POSTs (layout, instance) propagate
 * their failures so the caller can toast and abort, but the trailing workspace PUT is best-effort —
 * the instance already exists and is routable, and the open-time autosave re-persists it, so a
 * failed PUT must NOT reject (rejecting would strand a created instance and risk a duplicate retry).
 */
export async function instantiateInstanceFromLayout({
  name,
  config,
  workspace,
  createLayout,
  createInstance,
  saveWorkspace,
}: InstantiateInstanceArgs): Promise<{ instanceId: Uuid, slug: string }> {
  const layout = await createLayout(name, config)

  const instanceId = createUuid()
  const created = await createInstance({ instanceId, name, layoutId: layout.id })

  if (workspace) {
    try {
      await saveWorkspace(instanceId, workspace)
    } catch (error) {
      warn("New-instance workspace save failed; the open-time autosave will re-persist it", error)
    }
  }

  return { instanceId, slug: created.slug }
}

/**
 * Build the layout config + workspace snapshot for a fresh default dashboard.
 *
 * Sources the client default module set rather than the (unmerged) server starter seed: it
 * projects createDefaultModules() through the same export/serialize path the migration uses, so the
 * "+ New dashboard" instance is byte-identical to what a migrated default dashboard would produce.
 * Synthesizes a minimal slice state — only panels[].modules is populated; every other slice slot is
 * absent, so exportLayoutConfig/serializeWorkspace fall back to the per-module slice defaults.
 */
export function buildDefaultInstanceConfig(): { config: LayoutConfigV1, workspace: WorkspaceStateV1 } {
  const modules = createDefaultModules()
  const panels: DashboardPanelSlicesState["dashboard"]["panels"] = {
    [DEFAULTS_DASHBOARD_KEY]: { modules, isPreferencesLoaded: true },
  }
  // Only `panels` carries data; the other slice maps are empty so each panel resolves to its
  // slice-default viewConfig. Typed through WorkspaceSerializableState so both projections accept it.
  const state: WorkspaceSerializableState = {
    dashboard: {
      panels,
      assetView: {},
      factorsView: {},
      graph: {},
      kpi: {},
      core: {},
      model: {},
      trade: {},
      optimization: {},
    },
  }

  const { config } = exportLayoutConfig(state, DEFAULTS_DASHBOARD_KEY, null)
  const workspace = serializeWorkspace(state, DEFAULTS_DASHBOARD_KEY, null)
  return { config, workspace }
}
